// Mosaic overlay — fullscreen shader that samples a source render target
// per-cell and re-renders the frame as a grid of letter glyphs whose density
// (which letter) tracks the cell's luminance and whose color tracks its RGB.
//
// Used by the Engine as a post-scene step: generator renders into sceneRT,
// the overlay reads sceneRT.texture and writes a re-stylised version into
// its own RT, which then feeds the composer's blit pass.

import * as THREE from 'three';
import { buildTextAtlas } from './text-atlas.js';

const RAMP = ['.', ' ', 'I', 'Y', 'A', 'D'];

const GLYPHS = {
  'A': 'A.png',
  'I': 'I.png',
  ' ': '@.png',
  'D': 'D.png',
  'Y': 'Y.png',
  '.': 'dot.png',
};

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision highp float;

  uniform sampler2D tSource;
  uniform sampler2D tAtlas;
  uniform vec2 uGrid;
  uniform vec4 uRampUVs[6];
  uniform int uRampCount;
  uniform vec3 uTint;
  uniform float uEmissive;
  uniform float uMonochrome;
  uniform float uContrast;
  uniform float uBrightness;
  uniform float uInvert;
  uniform float uOpacity;
  uniform float uLetterSmoothing;
  uniform float uAspectFix;   // 0 = stretch cells to viewport, 1 = square cells
  uniform vec2 uResolution;

  varying vec2 vUv;

  vec4 rampRect(int idx) {
    vec4 rect = uRampUVs[0];
    for (int i = 0; i < 6; i++) {
      if (i == idx) rect = uRampUVs[i];
    }
    return rect;
  }

  void main() {
    // Build a square-cell grid that matches the source aspect when uAspectFix=1
    vec2 grid = uGrid;
    if (uAspectFix > 0.5) {
      float aspect = uResolution.x / uResolution.y;
      grid = vec2(uGrid.x, max(1.0, floor(uGrid.x / aspect)));
    }

    vec2 cellIdx = floor(vUv * grid);
    vec2 cellCenter = (cellIdx + 0.5) / grid;
    vec2 inCell = fract(vUv * grid);

    vec4 src = texture2D(tSource, cellCenter);
    float lum = dot(src.rgb, vec3(0.2126, 0.7152, 0.0722));
    lum = clamp((lum - 0.5) * uContrast + 0.5 + uBrightness, 0.0, 1.0);
    float ramp = mix(1.0 - lum, lum, uInvert);
    float rampPos = ramp * float(uRampCount);
    float basePos = floor(rampPos);
    float fracPos = fract(rampPos);
    int idxA = int(clamp(basePos, 0.0, float(uRampCount - 1)));
    int idxB = int(clamp(basePos + 1.0, 0.0, float(uRampCount - 1)));

    float edge = clamp(uLetterSmoothing * 0.5, 0.0, 0.5);
    float blend = 0.0;
    if (edge > 0.0 && idxA != idxB) {
      blend = smoothstep(1.0 - edge, 1.0, fracPos);
    }
    if (uLetterSmoothing >= 1.0 && idxA != idxB) {
      blend = smoothstep(0.0, 1.0, fracPos);
    }

    vec4 rectA = rampRect(idxA);
    vec4 rectB = rampRect(idxB);
    vec2 atlasUVA = mix(rectA.xy, rectA.zw, inCell);
    vec2 atlasUVB = mix(rectB.xy, rectB.zw, inCell);
    vec4 gA = texture2D(tAtlas, atlasUVA);
    vec4 gB = texture2D(tAtlas, atlasUVB);
    float maskA = max(max(max(gA.r, gA.g), gA.b), gA.a);
    float maskB = max(max(max(gB.r, gB.g), gB.b), gB.a);
    float mask = mix(maskA, maskB, blend);

    vec3 sampled = mix(src.rgb, vec3(1.0), uMonochrome);
    vec3 mosaic = uTint * sampled * mask * uEmissive;

    vec3 col = mix(src.rgb, mosaic, uOpacity);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class MosaicOverlay {
  constructor() {
    this.enabled = false;
    this.ready = false;

    this.params = {
      cols: 100,
      rows: 60,
      tint: '#6effd6',
      emissive: 1.0,
      monochrome: 1.0,
      contrast: 1.4,
      brightness: 0.0,
      invert: 0.0,
      opacity: 1.0,
      letterSmoothing: 0.0,
      aspectFix: 1.0,
    };

    this._initPromise = null;
    this.rt = null;
    this._pendingSize = null;
  }

  init() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = (async () => {
      const { texture: atlasTex, uvMap } = await buildTextAtlas(RAMP, { cellSize: 256 }, GLYPHS);

      const rampUVs = RAMP.map(ch => {
        const r = uvMap.get(ch) ?? { u0: 0, v0: 0, u1: 1, v1: 1 };
        return new THREE.Vector4(r.u0, r.v0, r.u1, r.v1);
      });

      this.atlasTexture = atlasTex;

      this.material = new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: {
          tSource:    { value: null },
          tAtlas:     { value: atlasTex },
          uGrid:      { value: new THREE.Vector2(this.params.cols, this.params.rows) },
          uRampUVs:   { value: rampUVs },
          uRampCount: { value: rampUVs.length },
          uTint:      { value: new THREE.Color(this.params.tint) },
          uEmissive:  { value: this.params.emissive },
          uMonochrome:{ value: this.params.monochrome },
          uContrast:  { value: this.params.contrast },
          uBrightness:{ value: this.params.brightness },
          uInvert:    { value: this.params.invert },
          uOpacity:   { value: this.params.opacity },
          uLetterSmoothing: { value: this.params.letterSmoothing },
          uAspectFix: { value: this.params.aspectFix },
          uResolution:{ value: new THREE.Vector2(2, 2) },
        },
        depthTest: false,
        depthWrite: false,
      });

      this.scene = new THREE.Scene();
      this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
      this.scene.add(this.quad);
      this.camera = new THREE.Camera();

      this.rt = new THREE.WebGLRenderTarget(2, 2, {
        type: THREE.UnsignedByteType,
        depthBuffer: false,
        stencilBuffer: false,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
      });
      this.rt.texture.generateMipmaps = false;

      this.ready = true;
      if (this._pendingSize) {
        this.setSize(this._pendingSize[0], this._pendingSize[1]);
        this._pendingSize = null;
      }
    })();
    return this._initPromise;
  }

  setSize(w, h) {
    if (!this.ready) {
      this._pendingSize = [w, h];
      return;
    }
    this.rt.setSize(w, h);
    this.material.uniforms.uResolution.value.set(w, h);
  }

  setParam(key, value) {
    if (!(key in this.params)) return;
    this.params[key] = value;
    if (!this.ready) return;
    const u = this.material.uniforms;
    switch (key) {
      case 'cols':
      case 'rows':       u.uGrid.value.set(this.params.cols, this.params.rows); break;
      case 'tint':       u.uTint.value.set(this.params.tint); break;
      case 'emissive':   u.uEmissive.value = value; break;
      case 'monochrome': u.uMonochrome.value = value; break;
      case 'contrast':   u.uContrast.value = value; break;
      case 'brightness': u.uBrightness.value = value; break;
      case 'invert':     u.uInvert.value = value; break;
      case 'opacity':    u.uOpacity.value = value; break;
      case 'letterSmoothing': u.uLetterSmoothing.value = value; break;
      case 'aspectFix':  u.uAspectFix.value = value; break;
    }
  }

  // Renders the mosaic from sourceTexture into this.rt, returns rt.texture.
  render(renderer, sourceTexture) {
    if (!this.ready) return sourceTexture;
    this.material.uniforms.tSource.value = sourceTexture;
    const prevTarget = renderer.getRenderTarget();
    renderer.setRenderTarget(this.rt);
    renderer.render(this.scene, this.camera);
    renderer.setRenderTarget(prevTarget);
    return this.rt.texture;
  }

  dispose() {
    this.rt?.dispose();
    this.material?.dispose();
    this.atlasTexture?.dispose();
  }
}
